import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { BaseInscription } from '../../../typings/third-party-approved-companies';
import { WindowInformation } from '../../../services/window-information';
import { IsUsedAsCloudProvider, thirdPartyApprovedCompaniesService } from '../../../services/central-api/third-party-approved-companies';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import FormActions from '../../../components/FormActions';
import { parseTextWithBold } from '../../deac/DeacCommons';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedInscription: BaseInscription;
    onConfirm: () => void;
}

export default function DeleteInscriptionModal({ selectedInscription, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    async function formSubmit() {
        try {
            setIsLoading(true);

            if (selectedInscription) {
                await thirdPartyApprovedCompaniesService.deleteInscriptions([selectedInscription.id]);

                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                onConfirm();
            }
        } catch (err) {
            if (err instanceof IsUsedAsCloudProvider) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('isUsedAsCloudProvider'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            closeModal();
        }
    }

    const closeModal = () => {
        setModalVisible(false);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <MyAppText style={styles.header}>{translate('attention')}</MyAppText>

                    <View style={{ flexDirection: 'column', rowGap: 4, alignItems: 'center' }}>
                        <MyAppText style={styles.subheader}>{translate('areYouSureYouWantToDeleteQuestion')}</MyAppText>
                        <MyAppText style={styles.subheader}>{parseTextWithBold(translate('theCompanyWillHaveItsInscriptionRemoved', { companyName: selectedInscription.companyName }))}</MyAppText>
                    </View>
                    <FormActions
                        viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText='goBack'
                        confirmText='delete'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={isLoading}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
    }),
    header: {
        alignSelf: 'center',
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
        textAlign: 'center',
    }
}));
